import React, { useState, useEffect } from 'react';
import api from '../../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import { 
  CreditCard, 
  CheckCircle, 
  AlertTriangle, 
  ExternalLink, 
  RefreshCw,
  Loader2,
  ArrowRight,
  Shield
} from 'lucide-react';

export default function StripeConnectDialog({ open, onOpenChange, onStatusChange }) {
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (open) {
      checkStatus();
    }
  }, [open]);

  // Check for return from Stripe onboarding
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('stripe_connect') === 'success') {
      toast.success('Stripe account connected successfully!');
      checkStatus();
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (urlParams.get('stripe_connect') === 'refresh') {
      toast.info('Please complete your Stripe account setup');
      handleRefreshLink();
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const res = await api.getStripeConnectStatus();
      setStatus(res.data);
      onStatusChange?.(res.data);
    } catch (error) {
      console.error('Failed to check Stripe Connect status:', error);
      toast.error('Failed to check payment account status');
    } finally {
      setLoading(false);
    }
  };

  const handleStartOnboarding = async () => {
    setConnecting(true);
    try {
      // Build return URL with query param to detect return
      const returnUrl = `${window.location.origin}/dashboard?tab=invoices&stripe_connect=success`;
      const res = await api.createStripeConnectAccount(returnUrl);
      // Redirect to Stripe onboarding
      window.location.href = res.data.onboarding_url;
    } catch (error) {
      console.error('Failed to start Stripe onboarding:', error);
      toast.error(error.response?.data?.detail || 'Failed to connect Stripe account');
      setConnecting(false);
    }
  };

  const handleRefreshLink = async () => {
    setConnecting(true);
    try {
      const returnUrl = `${window.location.origin}/dashboard?tab=invoices&stripe_connect=success`;
      const res = await api.refreshStripeConnectLink(returnUrl);
      window.location.href = res.data.onboarding_url;
    } catch (error) {
      console.error('Failed to refresh onboarding link:', error);
      toast.error(error.response?.data?.detail || 'Failed to get new onboarding link');
      setConnecting(false);
    }
  };

  const handleOpenDashboard = async () => {
    try {
      const res = await api.getStripeDashboardLink();
      window.open(res.data.dashboard_url, '_blank');
    } catch (error) {
      console.error('Failed to open Stripe dashboard:', error);
      toast.error('Failed to open Stripe dashboard');
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-blue-500 animate-spin mb-4" />
          <p className="text-slate-400">Checking account status...</p>
        </div>
      );
    }

    // Not connected - show onboarding
    if (!status?.connected || status?.status === 'not_started') {
      return (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="bg-blue-500/20 rounded-full p-3">
                <CreditCard className="h-6 w-6 text-blue-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-2">Accept Online Payments</h3>
                <p className="text-slate-300 text-sm mb-4">
                  Connect your Stripe account to let your clients pay invoices online with credit/debit cards. 
                  Payments go directly to your bank account.
                </p>
                <ul className="space-y-2 text-sm text-slate-400 mb-4">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Get paid faster with online payments
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Automatic payment tracking on invoices
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Direct deposits to your bank account
                  </li>
                  <li className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-green-400" />
                    Powered by Stripe's secure payment infrastructure
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <Button 
            onClick={handleStartOnboarding}
            disabled={connecting}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
          >
            {connecting ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                Connect with Stripe
                <ArrowRight className="h-5 w-5 ml-2" />
              </>
            )}
          </Button>

          <p className="text-xs text-slate-500 text-center">
            You'll be redirected to Stripe to complete the setup. This usually takes 5-10 minutes.
          </p>
        </div>
      );
    }

    // Pending - needs more info
    if (status?.status === 'pending' || !status?.charges_enabled) {
      return (
        <div className="space-y-6">
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="bg-yellow-500/20 rounded-full p-3">
                <AlertTriangle className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-2">Account Setup Incomplete</h3>
                <p className="text-slate-300 text-sm mb-4">
                  Your Stripe account needs additional information before you can accept payments.
                </p>
                {status?.requirements?.currently_due?.length > 0 && (
                  <div className="bg-slate-800/50 rounded p-3 mb-4">
                    <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Required Information:</p>
                    <ul className="text-sm text-slate-300 space-y-1">
                      {status.requirements.currently_due.slice(0, 5).map((req, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></span>
                          {req.replace(/_/g, ' ')}
                        </li>
                      ))}
                      {status.requirements.currently_due.length > 5 && (
                        <li className="text-slate-500">
                          +{status.requirements.currently_due.length - 5} more items
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          <Button 
            onClick={handleRefreshLink}
            disabled={connecting}
            className="w-full bg-yellow-600 hover:bg-yellow-700 text-white"
          >
            {connecting ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                Complete Setup
                <ExternalLink className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      );
    }

    // Active - fully connected
    if (status?.status === 'active' && status?.charges_enabled) {
      return (
        <div className="space-y-6">
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="bg-green-500/20 rounded-full p-3">
                <CheckCircle className="h-6 w-6 text-green-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-2">Online Payments Enabled</h3>
                <p className="text-slate-300 text-sm mb-4">
                  Your Stripe account is fully connected. Your clients can now pay invoices online!
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-slate-800/50 rounded p-3">
                    <p className="text-slate-400 text-xs uppercase tracking-wide mb-1">Card Payments</p>
                    <p className="text-green-400 font-medium flex items-center gap-1">
                      <CheckCircle className="h-4 w-4" /> Enabled
                    </p>
                  </div>
                  <div className="bg-slate-800/50 rounded p-3">
                    <p className="text-slate-400 text-xs uppercase tracking-wide mb-1">Payouts</p>
                    <p className={`font-medium flex items-center gap-1 ${status.payouts_enabled ? 'text-green-400' : 'text-yellow-400'}`}>
                      {status.payouts_enabled ? (
                        <><CheckCircle className="h-4 w-4" /> Enabled</>
                      ) : (
                        <><AlertTriangle className="h-4 w-4" /> Pending</>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button 
              onClick={handleOpenDashboard}
              variant="outline"
              className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Stripe Dashboard
            </Button>
            <Button 
              onClick={checkStatus}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          <p className="text-xs text-slate-500 text-center">
            A "Pay Now" button will automatically appear on your invoices for clients to pay online.
          </p>
        </div>
      );
    }

    // Restricted account
    if (status?.status === 'restricted') {
      return (
        <div className="space-y-6">
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="bg-red-500/20 rounded-full p-3">
                <AlertTriangle className="h-6 w-6 text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-2">Account Restricted</h3>
                <p className="text-slate-300 text-sm mb-4">
                  Your Stripe account has been restricted. Please visit the Stripe dashboard to resolve any issues.
                </p>
                {status?.requirements?.disabled_reason && (
                  <p className="text-red-400 text-sm">
                    Reason: {status.requirements.disabled_reason.replace(/_/g, ' ')}
                  </p>
                )}
              </div>
            </div>
          </div>

          <Button 
            onClick={handleOpenDashboard}
            className="w-full bg-red-600 hover:bg-red-700 text-white"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Open Stripe Dashboard
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 w-[95vw] max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Online Payments
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Accept credit card payments on your invoices
          </DialogDescription>
        </DialogHeader>

        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
